import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    Empty,
    Form,
    Input,
    Modal,
    Popconfirm,
    Radio,
    Select,
    Skeleton,
    Space,
    Switch,
    Table,
    Tag,
    Tooltip,
    Typography,
    message,
} from 'antd';
import {
    DeleteOutlined,
    EditOutlined,
    PlayCircleOutlined,
    PlusOutlined,
    StarFilled,
} from '@ant-design/icons';
import {
    aiAdminApi,
    SandboxRun,
    SandboxRunStatus,
    SandboxScenario,
    SandboxScenarioDetail,
    SubsystemId,
    SubsystemVersion,
} from '../../../services/ai-admin-api';
import { useGetAdminBrandsQuery } from '../../../services/nestjs-base-api';
import { useAuth } from '../../../hooks/useAuth';
import { ComparisonView } from '../sandbox/ComparisonView';
import { useSubsystemEditorContext } from '../hooks/useSubsystemDraftEditor';

const { Text, Paragraph } = Typography;

interface Props {
    subsystem: SubsystemId;
    jwt: string;
    /** Subsystems where the Sandbox runner doesn't apply (no LLM call). */
    notApplicable?: { reason: string };
}

const STATUS_COLOUR: Record<SandboxRunStatus, string> = {
    queued: 'blue',
    running: 'processing',
    complete: 'green',
    error: 'red',
};

interface BrandPickerProps {
    value?: number | null;
    onChange?: (value: number | null) => void;
    placeholder?: string;
    allowClear?: boolean;
}

const BrandPicker: React.FC<BrandPickerProps> = ({
    value,
    onChange,
    placeholder = 'Search brands by name…',
    allowClear = true,
}) => {
    const { user } = useAuth();
    const jwt = user?.jwtToken ?? '';
    const [search, setSearch] = useState('');
    const [debounced, setDebounced] = useState('');

    useEffect(() => {
        const t = setTimeout(() => setDebounced(search), 250);
        return () => clearTimeout(t);
    }, [search]);

    const { data, isFetching } = useGetAdminBrandsQuery(
        { jwtToken: jwt, page: 1, limit: 200, search: debounced || undefined },
        { skip: !jwt },
    );

    const options = useMemo(() => {
        const rows = data?.data ?? [];
        const out = rows.map((b) => ({
            value: b.id,
            label: `${b.name} — ${b.organisationName}`,
        }));
        // If the current value isn't in the page (likely after re-opening the
        // form for an edit), show its id as a placeholder option so it stays
        // selected until the user searches and finds the real row.
        if (value && !rows.some((r) => r.id === value)) {
            out.unshift({ value, label: `Brand #${value}` });
        }
        return out;
    }, [data, value]);

    return (
        <Select<number>
            showSearch
            allowClear={allowClear}
            placeholder={placeholder}
            value={value ?? undefined}
            onChange={(v) => onChange?.(v ?? null)}
            onSearch={setSearch}
            filterOption={false}
            loading={isFetching}
            options={options}
            notFoundContent={isFetching ? 'Searching…' : 'No matches'}
        />
    );
};

export const SandboxTab: React.FC<Props> = ({
    subsystem,
    jwt,
    notApplicable,
}) => {
    const [scenarios, setScenarios] = useState<SandboxScenario[]>([]);
    const [scenariosLoading, setScenariosLoading] = useState(false);
    const [runs, setRuns] = useState<SandboxRun[]>([]);
    const [runsLoading, setRunsLoading] = useState(false);
    const [versions, setVersions] = useState<SubsystemVersion[]>([]);
    const [editing, setEditing] = useState<{
        mode: 'create' | 'edit';
        initial?: SandboxScenarioDetail;
    } | null>(null);
    const [running, setRunning] = useState<{
        scenarioId: number | null;
        brandId: number | null;
        periodId: number | null;
        inputs?: Record<string, unknown> | null;
    } | null>(null);
    const [viewingRunId, setViewingRunId] = useState<number | null>(null);
    const [spend, setSpend] = useState<{
        last24hUsd: number;
        todayUsd: number;
        perSubsystem24h: Array<{
            subsystem: string;
            usd: number;
            runs: number;
        }>;
    } | null>(null);

    const reloadScenarios = useCallback(async () => {
        setScenariosLoading(true);
        try {
            setScenarios(await aiAdminApi.listSandboxScenarios(jwt, subsystem));
        } catch (err: any) {
            message.error(err.message ?? 'Failed to load scenarios');
        } finally {
            setScenariosLoading(false);
        }
    }, [jwt, subsystem]);

    const reloadRuns = useCallback(async () => {
        setRunsLoading(true);
        try {
            setRuns(await aiAdminApi.listSandboxRuns(jwt, subsystem, 50));
        } catch (err: any) {
            message.error(err.message ?? 'Failed to load runs');
        } finally {
            setRunsLoading(false);
        }
    }, [jwt, subsystem]);

    const reloadSpend = useCallback(async () => {
        try {
            setSpend(await aiAdminApi.getSandboxSpendSummary(jwt));
        } catch {
            /* non-fatal */
        }
    }, [jwt]);

    const reloadVersions = useCallback(async () => {
        try {
            setVersions(await aiAdminApi.listVersions(jwt, subsystem, 100));
        } catch {
            /* non-fatal — versions may be missing for very new subsystems */
        }
    }, [jwt, subsystem]);

    // Subscribe to the editor context so saving / publishing / discarding a
    // draft on the Configure tab refreshes the version picker — Antd Tabs
    // keeps mounted children alive when switching tabs, so without this
    // dependency the dropdown is stuck on the snapshot taken when Sandbox
    // first mounted.
    const editor = useSubsystemEditorContext();
    const draftVersionId = editor.detail?.draftVersionId ?? null;
    const publishedVersionId = editor.detail?.currentPublishedVersionId ?? null;

    useEffect(() => {
        if (notApplicable) return;
        reloadScenarios();
        reloadRuns();
        reloadVersions();
        reloadSpend();
    }, [
        notApplicable,
        reloadScenarios,
        reloadRuns,
        reloadVersions,
        reloadSpend,
        draftVersionId,
        publishedVersionId,
    ]);

    // Background poll for the recent-runs table while any row is still in a
    // non-terminal state. Phase 4d made the runner fire-and-forget, so a row
    // can flip from 'running' → 'complete' / 'error' at any time after the
    // initial fetch. Stops as soon as every visible run is in a terminal
    // state.
    useEffect(() => {
        if (notApplicable) return;
        const inFlight = runs.some(
            (r) => r.status === 'queued' || r.status === 'running',
        );
        if (!inFlight) {
            // No more runs in flight — refresh the spend tile in case one
            // just finished (cost numbers come from completed runs).
            reloadSpend();
            return;
        }
        const id = setTimeout(() => {
            reloadRuns();
        }, 2500);
        return () => clearTimeout(id);
    }, [runs, notApplicable, reloadRuns, reloadSpend]);

    /**
     * Sandbox compares ANY two versions, not just two published ones — the
     * primary use case is "draft vs current published" so the admin can test
     * a change BEFORE hitting publish. We surface drafts + published +
     * superseded in the picker (discarded/rolled-back are excluded as not
     * useful comparison targets).
     */
    const selectableVersions = useMemo(
        () =>
            versions.filter(
                (v) =>
                    v.status === 'draft' ||
                    v.status === 'published' ||
                    v.status === 'superseded',
            ),
        [versions],
    );

    if (notApplicable) {
        return (
            <Alert
                type="info"
                showIcon
                message="Sandbox not applicable"
                description={notApplicable.reason}
            />
        );
    }

    return (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Alert
                type="info"
                showIcon
                message="Compare two configurations side-by-side"
                description={
                    <span>
                        Pick a scenario (or run live with the brand picker), choose
                        two versions to compare, then run. Token usage is attributed
                        to the platform-admin org so it doesn&apos;t count against
                        any customer&apos;s tier quota.
                    </span>
                }
            />

            {spend && (
                <Card
                    size="small"
                    title="Sandbox spend"
                    styles={{ body: { padding: 12 } }}
                >
                    <Space size="large" wrap>
                        <Space direction="vertical" size={2}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                Today
                            </Text>
                            <Text strong style={{ fontSize: 18 }}>
                                ${spend.todayUsd.toFixed(4)}
                            </Text>
                        </Space>
                        <Space direction="vertical" size={2}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                Last 24h (rolling)
                            </Text>
                            <Text strong style={{ fontSize: 18 }}>
                                ${spend.last24hUsd.toFixed(4)}
                            </Text>
                        </Space>
                        {spend.perSubsystem24h.length > 0 && (
                            <Space direction="vertical" size={2}>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    By subsystem (last 24h)
                                </Text>
                                <Space size="small" wrap>
                                    {spend.perSubsystem24h.map((s) => (
                                        <Tag key={s.subsystem}>
                                            {s.subsystem}: $
                                            {s.usd.toFixed(4)} ({s.runs}{' '}
                                            run{s.runs === 1 ? '' : 's'})
                                        </Tag>
                                    ))}
                                </Space>
                            </Space>
                        )}
                    </Space>
                </Card>
            )}

            <Card
                title={<span><StarFilled style={{ marginRight: 8 }} />Scenarios</span>}
                extra={
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => setEditing({ mode: 'create' })}
                    >
                        New scenario
                    </Button>
                }
                size="small"
                styles={{ body: { padding: 12 } }}
            >
                {scenariosLoading && scenarios.length === 0 ? (
                    <Skeleton active />
                ) : scenarios.length === 0 ? (
                    <Empty description="No scenarios yet — create one or run ad-hoc using the brand picker below.">
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => setEditing({ mode: 'create' })}
                        >
                            New scenario
                        </Button>
                    </Empty>
                ) : (
                    <Table<SandboxScenario>
                        size="small"
                        rowKey="id"
                        pagination={false}
                        dataSource={scenarios}
                        columns={[
                            {
                                title: 'Name',
                                dataIndex: 'name',
                                render: (name: string, row) => (
                                    <Space size={4}>
                                        {row.isCurated && (
                                            <Tooltip title="Curated">
                                                <StarFilled style={{ color: '#faad14' }} />
                                            </Tooltip>
                                        )}
                                        <Text strong>{name}</Text>
                                    </Space>
                                ),
                            },
                            {
                                title: 'Brand',
                                dataIndex: 'brandName',
                                render: (v: string | null, row) =>
                                    v ? (
                                        <Text>{v}</Text>
                                    ) : row.brandId ? (
                                        <Text type="secondary">#{row.brandId}</Text>
                                    ) : (
                                        <Text type="secondary">—</Text>
                                    ),
                                width: 180,
                            },
                            {
                                title: 'Updated',
                                dataIndex: 'updatedAt',
                                render: (v: string) =>
                                    new Date(v).toLocaleString(),
                                width: 180,
                            },
                            {
                                title: 'Actions',
                                key: 'actions',
                                width: 240,
                                render: (_, row) => (
                                    <Space size="small">
                                        <Button
                                            size="small"
                                            type="primary"
                                            icon={<PlayCircleOutlined />}
                                            onClick={async () => {
                                                void reloadVersions();
                                                // Fetch the scenario's full
                                                // detail so we can prefill
                                                // subsystem-specific inputs.
                                                let inputs: Record<string, unknown> | null = null;
                                                try {
                                                    const detail =
                                                        await aiAdminApi.getSandboxScenario(
                                                            jwt,
                                                            subsystem,
                                                            row.id,
                                                        );
                                                    inputs =
                                                        (detail.inputsJson as Record<string, unknown> | null) ??
                                                        null;
                                                } catch {
                                                    /* non-fatal — fall back to defaults */
                                                }
                                                setRunning({
                                                    scenarioId: row.id,
                                                    brandId: row.brandId,
                                                    periodId: row.periodId,
                                                    inputs,
                                                });
                                            }
                                            }
                                        >
                                            Run
                                        </Button>
                                        <Button
                                            size="small"
                                            icon={<EditOutlined />}
                                            onClick={async () => {
                                                const detail =
                                                    await aiAdminApi.getSandboxScenario(
                                                        jwt,
                                                        subsystem,
                                                        row.id,
                                                    );
                                                setEditing({
                                                    mode: 'edit',
                                                    initial: detail,
                                                });
                                            }}
                                        >
                                            Edit
                                        </Button>
                                        <Popconfirm
                                            title="Delete this scenario?"
                                            okText="Delete"
                                            okButtonProps={{ danger: true }}
                                            onConfirm={async () => {
                                                try {
                                                    await aiAdminApi.deleteSandboxScenario(
                                                        jwt,
                                                        subsystem,
                                                        row.id,
                                                    );
                                                    message.success('Scenario deleted');
                                                    reloadScenarios();
                                                } catch (err: any) {
                                                    message.error(
                                                        err.message ?? 'Delete failed',
                                                    );
                                                }
                                            }}
                                        >
                                            <Button
                                                size="small"
                                                danger
                                                icon={<DeleteOutlined />}
                                            />
                                        </Popconfirm>
                                    </Space>
                                ),
                            },
                        ]}
                    />
                )}
            </Card>

            <Card
                title="Ad-hoc run"
                size="small"
                styles={{ body: { padding: 12 } }}
            >
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Text type="secondary">
                        Run without saving a scenario. Pulls live data for the brand
                        you pick.
                    </Text>
                    <Button
                        icon={<PlayCircleOutlined />}
                        onClick={() => {
                            void reloadVersions();
                            setRunning({
                                scenarioId: null,
                                brandId: null,
                                periodId: null,
                            });
                        }}
                    >
                        Run with live brand picker
                    </Button>
                </Space>
            </Card>

            <Card
                title="Recent runs"
                size="small"
                styles={{ body: { padding: 12 } }}
            >
                {runsLoading && runs.length === 0 ? (
                    <Skeleton active />
                ) : runs.length === 0 ? (
                    <Empty description="No sandbox runs for this subsystem yet." />
                ) : (
                    <Table<SandboxRun>
                        size="small"
                        rowKey="id"
                        pagination={{ pageSize: 10 }}
                        dataSource={runs}
                        onRow={(row) => ({
                            onClick: () => setViewingRunId(row.id),
                            style: { cursor: 'pointer' },
                        })}
                        columns={[
                            {
                                title: 'Run',
                                dataIndex: 'id',
                                width: 80,
                                render: (id: number) => `#${id}`,
                            },
                            {
                                title: 'Status',
                                dataIndex: 'status',
                                width: 100,
                                render: (status: SandboxRunStatus) => (
                                    <Tag color={STATUS_COLOUR[status]}>
                                        {status}
                                    </Tag>
                                ),
                            },
                            {
                                title: 'Versions',
                                key: 'versions',
                                render: (_, row) =>
                                    `v${row.configAVersionNumber ?? '?'} vs v${
                                        row.configBVersionNumber ?? '?'
                                    }`,
                                width: 120,
                            },
                            {
                                title: 'Scenario',
                                dataIndex: 'scenarioName',
                                render: (v: string | null) =>
                                    v ? (
                                        <Text>{v}</Text>
                                    ) : (
                                        <Text type="secondary">Ad-hoc</Text>
                                    ),
                                width: 200,
                            },
                            {
                                title: 'Started',
                                dataIndex: 'createdAt',
                                width: 180,
                                render: (v: string) => new Date(v).toLocaleString(),
                            },
                            {
                                title: 'By',
                                dataIndex: 'createdByName',
                                width: 160,
                                render: (v: string | null) =>
                                    v ?? <Text type="secondary">—</Text>,
                            },
                            {
                                title: 'Error',
                                dataIndex: 'errorMessage',
                                ellipsis: true,
                                render: (v: string | null) =>
                                    v ? (
                                        <Tooltip title={v}>
                                            <Text type="danger" ellipsis>
                                                {v}
                                            </Text>
                                        </Tooltip>
                                    ) : (
                                        <Text type="secondary">—</Text>
                                    ),
                            },
                        ]}
                    />
                )}
            </Card>

            {editing && (
                <ScenarioFormModal
                    subsystem={subsystem}
                    jwt={jwt}
                    mode={editing.mode}
                    initial={editing.initial}
                    onClose={() => setEditing(null)}
                    onSaved={() => {
                        setEditing(null);
                        reloadScenarios();
                    }}
                />
            )}

            {running && (
                <RunFormModal
                    subsystem={subsystem}
                    jwt={jwt}
                    initial={running}
                    initialScenarioInputs={running.inputs ?? null}
                    selectableVersions={selectableVersions}
                    onClose={() => setRunning(null)}
                    onAfterSubmit={(runId) => {
                        setRunning(null);
                        reloadRuns();
                        if (runId != null) setViewingRunId(runId);
                    }}
                />
            )}

            <ComparisonView
                runId={viewingRunId}
                subsystem={subsystem}
                jwt={jwt}
                onClose={() => setViewingRunId(null)}
            />
        </Space>
    );
};

interface ScenarioFormProps {
    subsystem: SubsystemId;
    jwt: string;
    mode: 'create' | 'edit';
    initial?: SandboxScenarioDetail;
    onClose: () => void;
    onSaved: () => void;
}

const ScenarioFormModal: React.FC<ScenarioFormProps> = ({
    subsystem,
    jwt,
    mode,
    initial,
    onClose,
    onSaved,
}) => {
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const inputs =
            (initial?.inputsJson as Record<string, unknown> | null) ?? {};
        const subsystemDefaults: Record<string, unknown> = {
            period: '180_days',
            userMessage: '',
            expertType:
                subsystem === 'advice_ai'
                    ? 'advice_key_findings'
                    : subsystem === 'orbital_ai'
                      ? 'orbital_findings'
                      : undefined,
            elementName:
                subsystem === 'building_blocks_ai' ||
                subsystem === 'element_analysis'
                    ? 'Willingness to Pay a Premium'
                    : undefined,
            elementType:
                subsystem === 'element_analysis' ? 'latent' : undefined,
        };
        form.setFieldsValue({
            name: initial?.name ?? '',
            description: initial?.description ?? '',
            brandId: initial?.brandId ?? undefined,
            isCurated: !!initial?.isCurated,
            ...subsystemDefaults,
            ...inputs,
        });
    }, [form, initial, subsystem]);

    const submit = async () => {
        const values = await form.validateFields();
        // Strip the top-level scenario fields out of subsystem inputs so
        // inputsJson holds only what gets passed to the runner.
        const inputs: Record<string, unknown> = {};
        for (const key of Object.keys(values)) {
            if (
                key === 'name' ||
                key === 'description' ||
                key === 'brandId' ||
                key === 'isCurated'
            )
                continue;
            const v = values[key];
            if (v !== undefined && v !== null && v !== '') {
                inputs[key] = v;
            }
        }
        const body = {
            name: values.name.trim(),
            description: values.description?.trim() || undefined,
            brandId: values.brandId,
            periodId: null,
            inputsJson: Object.keys(inputs).length > 0 ? inputs : null,
            isCurated: !!values.isCurated,
        };
        setSubmitting(true);
        try {
            if (mode === 'create') {
                await aiAdminApi.createSandboxScenario(jwt, subsystem, body);
                message.success('Scenario created');
            } else if (initial) {
                await aiAdminApi.updateSandboxScenario(
                    jwt,
                    subsystem,
                    initial.id,
                    body,
                );
                message.success('Scenario updated');
            }
            onSaved();
        } catch (err: any) {
            message.error(err.message ?? 'Save failed');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            open
            width={640}
            title={mode === 'create' ? 'New scenario' : `Edit "${initial?.name}"`}
            okText={mode === 'create' ? 'Create' : 'Save'}
            onCancel={onClose}
            onOk={submit}
            confirmLoading={submitting}
        >
            <Form form={form} layout="vertical">
                <Form.Item
                    label="Name"
                    name="name"
                    rules={[{ required: true, message: 'Name is required' }]}
                >
                    <Input placeholder="e.g. Q4 Tesla — pre-launch baseline" />
                </Form.Item>

                <Form.Item label="Description (optional)" name="description">
                    <Input.TextArea
                        rows={2}
                        placeholder="What is this scenario useful for testing?"
                    />
                </Form.Item>

                <Form.Item
                    label="Brand"
                    name="brandId"
                    rules={[{ required: true, message: 'Pick a brand' }]}
                >
                    <BrandPicker allowClear={false} />
                </Form.Item>

                <SubsystemInputs
                    subsystem={subsystem}
                    jwt={jwt}
                    form={form}
                />

                <Form.Item label="Curated" name="isCurated" valuePropName="checked">
                    <Switch />
                </Form.Item>
                <Paragraph type="secondary" style={{ marginTop: -8 }}>
                    Curated scenarios sort to the top of the list. Use for the
                    canonical "did we regress?" tests.
                </Paragraph>
            </Form>
        </Modal>
    );
};

interface RunFormProps {
    subsystem: SubsystemId;
    jwt: string;
    initial: {
        scenarioId: number | null;
        brandId: number | null;
        periodId: number | null;
    };
    /**
     * When launched from a saved scenario, the parent passes the scenario's
     * inputsJson here so the form prefills period + subsystem-specific inputs.
     * Admin can still tweak any of them per-run.
     */
    initialScenarioInputs?: Record<string, unknown> | null;
    selectableVersions: SubsystemVersion[];
    onClose: () => void;
    onAfterSubmit: (runId: number | null) => void;
}

const VERSION_STATUS_COLOUR: Record<string, string> = {
    draft: 'orange',
    published: 'green',
    superseded: 'default',
    rolled_back: 'red',
    discarded: 'default',
};

function versionLabel(v: SubsystemVersion): React.ReactNode {
    const tag = (
        <Tag color={VERSION_STATUS_COLOUR[v.status]} style={{ marginRight: 4 }}>
            {v.status === 'published'
                ? `published v${v.versionNumber}`
                : v.status === 'draft'
                  ? 'draft'
                  : `v${v.versionNumber} ${v.status}`}
        </Tag>
    );
    return (
        <span>
            {tag}
            <Text type="secondary">
                {v.notes?.slice(0, 60) ?? '(no notes)'}
            </Text>
        </span>
    );
}

const PERIOD_OPTIONS = [
    { value: '30_days', label: 'Last 30 days' },
    { value: '60_days', label: 'Last 60 days' },
    { value: '90_days', label: 'Last 90 days' },
    { value: '120_days', label: 'Last 120 days' },
    { value: '180_days', label: 'Last 180 days' },
    { value: '365_days', label: 'Last 365 days' },
];

const ADVICE_EXPERT_OPTIONS = [
    { value: 'advice_key_findings', label: 'Key Findings' },
    { value: 'advice_explore_further', label: 'Explore Further' },
    { value: 'advice_opportunities', label: 'Opportunities' },
    { value: 'advice_threats', label: 'Threats' },
    { value: 'advice_notable_changes', label: 'Notable Changes' },
];

const ORBITAL_EXPERT_OPTIONS = [
    { value: 'orbital_findings', label: 'Findings (synthesises Key Findings)' },
    { value: 'orbital_actions', label: 'Actions (synthesises Opportunities)' },
];

// Briefing types — one row per config_key in briefing_ai_config. Sandbox runs
// one briefing type at a time (mirrors the per-type prompt config).
const BRIEFING_TYPE_OPTIONS = [
    { value: 'one_thing', label: 'One thing to know' },
    { value: 'the_fix', label: 'The fix' },
    { value: 'recommendations', label: 'Recommendations' },
    { value: 'boosters_summary', label: 'Boosters summary' },
];

const BUILDING_BLOCKS_ELEMENT_OPTIONS = [
    { value: 'Willingness to Pay a Premium', label: 'Willingness to Pay a Premium' },
    { value: 'Brand Attachment', label: 'Brand Attachment' },
    { value: 'Quality Value Tradeoff (PQV)', label: 'Quality Value Tradeoff (PQV)' },
    { value: 'Alignment Depth', label: 'Alignment Depth' },
    { value: 'Behavioural Loyalty', label: 'Behavioural Loyalty' },
    { value: 'Attitudinal Loyalty', label: 'Attitudinal Loyalty' },
    { value: 'Market Awareness', label: 'Market Awareness' },
    { value: 'Associations', label: 'Associations' },
];

// Latent elements: same fixed 8 the brand equity model produces. Used by the
// Element Analysis sandbox alongside Building Blocks.
const LATENT_ELEMENT_OPTIONS = BUILDING_BLOCKS_ELEMENT_OPTIONS;

/**
 * Renders the period + subsystem-specific input fields shared by both the
 * Scenario form and the Run modal. Owns its own dynamic fetches (AI Experts
 * list, measured categories) keyed off the form's brandId + period values
 * so the consumer doesn't have to manage that state.
 *
 * Field names assigned to the form: `period`, `expertType`, `elementName`,
 * `elementType`, `userMessage`. Consumers read them via form.getFieldsValue()
 * or Form.useWatch.
 */
const SubsystemInputs: React.FC<{
    subsystem: SubsystemId;
    jwt: string;
    form: ReturnType<typeof Form.useForm>[0];
}> = ({ subsystem, jwt, form }) => {
    const watchedBrandId = Form.useWatch('brandId', form);
    const watchedPeriod = Form.useWatch('period', form);
    const watchedElementType = Form.useWatch('elementType', form);

    const [aiExperts, setAiExperts] = useState<
        Array<{ expertType: string; name: string }> | null
    >(null);
    const [measuredCategories, setMeasuredCategories] = useState<string[] | null>(
        null,
    );
    const [loadingCategories, setLoadingCategories] = useState(false);

    useEffect(() => {
        if (subsystem !== 'ai_experts') return;
        let cancelled = false;
        aiAdminApi
            .listSandboxExperts(jwt, subsystem)
            .then((res) => {
                if (!cancelled) setAiExperts(res.experts);
            })
            .catch((err) => {
                if (cancelled) return;
                message.error(err.message ?? 'Failed to load experts');
                setAiExperts([]);
            });
        return () => {
            cancelled = true;
        };
    }, [subsystem, jwt]);

    useEffect(() => {
        if (subsystem !== 'element_analysis') return;
        if (watchedElementType !== 'measured') return;
        if (!watchedBrandId || !watchedPeriod) {
            setMeasuredCategories(null);
            return;
        }
        let cancelled = false;
        setLoadingCategories(true);
        aiAdminApi
            .listMeasuredCategoriesForBrand(
                jwt,
                subsystem,
                watchedBrandId,
                watchedPeriod,
            )
            .then((res) => {
                if (!cancelled) setMeasuredCategories(res.categories);
            })
            .catch((err) => {
                if (cancelled) return;
                message.error(err.message ?? 'Failed to load categories');
                setMeasuredCategories([]);
            })
            .finally(() => {
                if (!cancelled) setLoadingCategories(false);
            });
        return () => {
            cancelled = true;
        };
    }, [subsystem, jwt, watchedBrandId, watchedPeriod, watchedElementType]);

    return (
        <>
            <Form.Item
                label="Period"
                name="period"
                rules={[{ required: true, message: 'Pick a period' }]}
            >
                <Select options={PERIOD_OPTIONS} />
            </Form.Item>

            {subsystem === 'advice_ai' && (
                <Form.Item
                    label="Expert persona"
                    name="expertType"
                    rules={[{ required: true, message: 'Pick a persona' }]}
                    extra="Each Advice AI persona has its own prompt, so you compare one persona at a time. Notable Changes triggers tool calls to fetch data across periods, so it'll be slower and run two LLM calls per side."
                >
                    <Select options={ADVICE_EXPERT_OPTIONS} />
                </Form.Item>
            )}

            {subsystem === 'orbital_ai' && (
                <Form.Item
                    label="Orbital expert"
                    name="expertType"
                    rules={[{ required: true, message: 'Pick an orbital expert' }]}
                    extra="Orbital reads from the matching Advice AI cache. The runner regenerates upstream advice if it's missing or stale, so the first run for a brand may take a few extra seconds."
                >
                    <Select options={ORBITAL_EXPERT_OPTIONS} />
                </Form.Item>
            )}

            {subsystem === 'briefing_ai' && (
                <Form.Item
                    label="Briefing type"
                    name="configKey"
                    rules={[{ required: true, message: 'Pick a briefing type' }]}
                    extra="Each briefing type has its own prompt in briefing_ai_config, so you compare one type at a time. Both sides see the same brand-equity summary as input."
                >
                    <Select options={BRIEFING_TYPE_OPTIONS} />
                </Form.Item>
            )}

            {subsystem === 'building_blocks_ai' && (
                <Form.Item
                    label="Element"
                    name="elementName"
                    rules={[{ required: true, message: 'Pick an element' }]}
                    extra="Sandbox runs in individual mode (one element at a time). If a version doesn't have a per-element prompt, the runner falls back to the 'default' configKey row, mirroring the prod lookup chain."
                >
                    <Select options={BUILDING_BLOCKS_ELEMENT_OPTIONS} />
                </Form.Item>
            )}

            {subsystem === 'ai_experts' && (
                <>
                    <Form.Item
                        label="Expert"
                        name="expertType"
                        rules={[{ required: true, message: 'Pick an expert' }]}
                        extra="Loaded from the live ai_experts table. The runner pulls the persona_prompt + llm_model + llm_temperature from each version's payload for that expert."
                    >
                        <Select
                            placeholder={
                                aiExperts === null
                                    ? 'Loading experts…'
                                    : 'Pick an expert'
                            }
                            loading={aiExperts === null}
                            options={(aiExperts ?? []).map((e) => ({
                                value: e.expertType,
                                label: e.name,
                            }))}
                        />
                    </Form.Item>
                    <Form.Item
                        label="User message"
                        name="userMessage"
                        rules={[{ required: true, message: 'Type a question' }]}
                        extra="The message you'd send the expert. Each side will receive this verbatim alongside the brand-equity context block and its version's persona prompt."
                    >
                        <Input.TextArea
                            rows={4}
                            placeholder="e.g. What's driving the drop in our brand attachment score this quarter?"
                        />
                    </Form.Item>
                </>
            )}

            {subsystem === 'element_analysis' && (
                <>
                    <Form.Item
                        label="Element type"
                        name="elementType"
                        rules={[{ required: true }]}
                        extra="Measured = a question category (e.g. Brand Trust, Product Performance). Latent = a derived element (the 8 that feed Building Blocks). Each side will be analysed using the prompt template for that type."
                    >
                        <Radio.Group
                            optionType="button"
                            options={[
                                { label: 'Measured', value: 'measured' },
                                { label: 'Latent', value: 'latent' },
                            ]}
                            onChange={() => {
                                form.setFieldValue('elementName', undefined);
                            }}
                        />
                    </Form.Item>
                    <Form.Item
                        shouldUpdate={(prev, curr) =>
                            prev.elementType !== curr.elementType
                        }
                        noStyle
                    >
                        {({ getFieldValue }) => {
                            const t = getFieldValue('elementType');
                            if (t === 'latent') {
                                return (
                                    <Form.Item
                                        label="Latent element"
                                        name="elementName"
                                        rules={[
                                            { required: true, message: 'Pick an element' },
                                        ]}
                                    >
                                        <Select options={LATENT_ELEMENT_OPTIONS} />
                                    </Form.Item>
                                );
                            }
                            return (
                                <Form.Item
                                    label="Measured category"
                                    name="elementName"
                                    rules={[{ required: true, message: 'Pick a category' }]}
                                    extra={
                                        !watchedBrandId
                                            ? "Pick a brand above first — categories are loaded from that brand's question categories."
                                            : measuredCategories?.length === 0
                                              ? 'This brand has no measured categories for the chosen period.'
                                              : "Categories come from this brand's most-recent brand-equity calculation for the selected period."
                                    }
                                >
                                    <Select
                                        showSearch
                                        placeholder={
                                            loadingCategories
                                                ? 'Loading categories…'
                                                : 'Pick a category'
                                        }
                                        loading={loadingCategories}
                                        options={(measuredCategories ?? []).map((c) => ({
                                            value: c,
                                            label: c,
                                        }))}
                                        disabled={!watchedBrandId || !watchedPeriod}
                                        notFoundContent={
                                            loadingCategories
                                                ? 'Loading…'
                                                : !watchedBrandId
                                                  ? 'Pick a brand first'
                                                  : 'No categories'
                                        }
                                    />
                                </Form.Item>
                            );
                        }}
                    </Form.Item>
                </>
            )}
        </>
    );
};


const RunFormModal: React.FC<RunFormProps> = ({
    subsystem,
    jwt,
    initial,
    initialScenarioInputs,
    selectableVersions,
    onClose,
    onAfterSubmit,
}) => {
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);
    const [estimate, setEstimate] = useState<{
        averageCostUsd: number | null;
        sampleSize: number;
    } | null>(null);

    useEffect(() => {
        let cancelled = false;
        aiAdminApi
            .estimateSandboxCost(jwt, subsystem)
            .then((res) => {
                if (!cancelled) setEstimate(res);
            })
            .catch(() => {
                /* non-fatal */
            });
        return () => {
            cancelled = true;
        };
    }, [jwt, subsystem]);

    useEffect(() => {
        // Smart defaults — prioritise the most useful comparison: current
        // published as A, draft as B (so the admin can validate the draft
        // before hitting Publish). Falls back to current published vs prior
        // published when no draft exists.
        const draft = selectableVersions.find((v) => v.status === 'draft');
        const published = selectableVersions.filter(
            (v) => v.status === 'published',
        );
        const supersededOrPublished = selectableVersions.filter(
            (v) => v.status === 'published' || v.status === 'superseded',
        );
        const currentPublished = published[0];
        const priorPublished =
            supersededOrPublished.find((v) => v.id !== currentPublished?.id) ??
            null;

        const defaultA = currentPublished?.id ?? selectableVersions[0]?.id;
        const defaultB =
            (draft?.id ?? priorPublished?.id) ??
            selectableVersions.find((v) => v.id !== defaultA)?.id;

        // Build initial values: defaults first, then overlay scenario inputs
        // (when launched from a saved scenario) so prefilled fields beat the
        // generic defaults.
        const scenarioInputs = initialScenarioInputs ?? {};
        const defaults: Record<string, unknown> = {
            scenarioId: initial.scenarioId ?? undefined,
            brandId: initial.brandId ?? undefined,
            period: '180_days',
            userMessage: '',
            expertType:
                subsystem === 'advice_ai'
                    ? 'advice_key_findings'
                    : subsystem === 'orbital_ai'
                      ? 'orbital_findings'
                      : undefined,
            elementName:
                subsystem === 'building_blocks_ai'
                    ? 'Willingness to Pay a Premium'
                    : subsystem === 'element_analysis'
                      ? 'Willingness to Pay a Premium'
                      : undefined,
            elementType:
                subsystem === 'element_analysis' ? 'latent' : undefined,
            configAVersionId: defaultA,
            configBVersionId: defaultB,
        };
        form.setFieldsValue({ ...defaults, ...scenarioInputs });
    }, [form, initial, initialScenarioInputs, selectableVersions, subsystem]);

    const submit = async () => {
        const values = await form.validateFields();
        if (values.configAVersionId === values.configBVersionId) {
            message.error('Pick two different versions to compare.');
            return;
        }
        setSubmitting(true);
        try {
            const inputs: Record<string, unknown> = {
                period: values.period ?? '180_days',
            };
            if (subsystem === 'advice_ai' || subsystem === 'orbital_ai') {
                inputs.expertType = values.expertType;
            }
            if (subsystem === 'briefing_ai') {
                inputs.configKey = values.configKey;
            }
            if (subsystem === 'building_blocks_ai') {
                inputs.elementName = values.elementName;
            }
            if (subsystem === 'element_analysis') {
                inputs.elementType = values.elementType;
                inputs.elementName = values.elementName;
            }
            if (subsystem === 'ai_experts') {
                inputs.expertType = values.expertType;
                inputs.userMessage = values.userMessage;
            }
            const run = await aiAdminApi.createSandboxRun(jwt, subsystem, {
                scenarioId: initial.scenarioId,
                brandId: values.brandId ?? null,
                periodId: null,
                inputs,
                configAVersionId: values.configAVersionId,
                configBVersionId: values.configBVersionId,
            });
            // Phase 4d: the runner is fire-and-forget on the server. POST
            // returns the queued run immediately; the comparison drawer
            // opens with that id and polls until the run reaches a terminal
            // state. The submit button is no longer the bottleneck.
            message.success(`Run #${run.id} started`);
            onAfterSubmit(run.id);
        } catch (err: any) {
            message.error(err.message ?? 'Run failed');
            onAfterSubmit(null);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            open
            width={560}
            title="Run sandbox comparison"
            okText={submitting ? 'Running…' : 'Run'}
            onCancel={onClose}
            onOk={submit}
            confirmLoading={submitting}
            cancelButtonProps={{ disabled: submitting }}
            closable={!submitting}
            maskClosable={!submitting}
        >
            <Alert
                type="info"
                showIcon
                message="Run will execute in the background"
                description={
                    <span>
                        The comparison drawer opens immediately and fills in
                        as each side completes. You can close the modal as
                        soon as the run starts.
                        {estimate &&
                            estimate.averageCostUsd !== null &&
                            estimate.averageCostUsd > 0 && (
                                <>
                                    {' '}
                                    Estimated cost (avg of last{' '}
                                    {estimate.sampleSize} runs):{' '}
                                    <Text strong>
                                        ~${estimate.averageCostUsd.toFixed(4)}
                                    </Text>{' '}
                                    (both sides combined).
                                </>
                            )}
                        {estimate && estimate.sampleSize === 0 && (
                            <>
                                {' '}
                                <Text type="secondary">
                                    No cost estimate yet — first run for this
                                    subsystem will establish a baseline.
                                </Text>
                            </>
                        )}
                    </span>
                }
                style={{ marginBottom: 16 }}
            />

            <Form form={form} layout="vertical" disabled={submitting}>
                {initial.scenarioId === null && (
                    <Form.Item
                        label="Brand"
                        name="brandId"
                        rules={[{ required: true, message: 'Pick a brand' }]}
                    >
                        <BrandPicker />
                    </Form.Item>
                )}
                <SubsystemInputs
                    subsystem={subsystem}
                    jwt={jwt}
                    form={form}
                />

                <Form.Item
                    label="Version A (baseline)"
                    name="configAVersionId"
                    rules={[{ required: true }]}
                    extra="Defaults to the current published version."
                >
                    <Select
                        placeholder="Pick baseline version"
                        options={selectableVersions.map((v) => ({
                            value: v.id,
                            label: versionLabel(v),
                        }))}
                    />
                </Form.Item>

                <Form.Item
                    label="Version B (test candidate)"
                    name="configBVersionId"
                    rules={[{ required: true }]}
                    extra="Defaults to the pending draft if one exists — that's the typical 'test before publish' workflow."
                >
                    <Select
                        placeholder="Pick candidate version"
                        options={selectableVersions.map((v) => ({
                            value: v.id,
                            label: versionLabel(v),
                        }))}
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
};
